import React, {useContext, useEffect, useRef, useState} from "react";
import PropTypes from 'prop-types';
import {Box, Button, Stack, Tab, Tabs, TextField, Typography} from "@mui/material";
import {ce_default_url} from "../../context/firebase-config";
import {authContext} from "../../context/authContext";
import CorporateRiskSummary from "./CorporateRiskSummary";
import './CorporateRisk.css';
import CorporateExposureSummary from "./CorporateExposureSummary";
import CorporateResilienceSummary from "./CorporateResilienceSummary";
import CorporateRisk from "./CorporateRisk";

const CorporateRiskDashboard = ({project}) => {
    const user = useContext(authContext);
    const [query, setQuery] = useState('');
    const [companies, setCompanies] = useState([]);
    const [searchTerm, setSearchTerm] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const chartRef = useRef(null);

    const [value, setValue] = useState(0);

    useEffect(() => {
        if (!searchTerm) return;
        fetchCorporateRisk(user, project, searchTerm, setCompanies, setIsLoading);
    }, [project, searchTerm, user]);

    const corporateRiskTabs = getCorporateRiskTabs(user, project, companies, value, setValue, isLoading, chartRef);
    return (
        <div id="content" className={"bg-black"} style={{
            width: '65%', margin: "10px auto 0 auto", padding: "0 15px", fontSize: "1rem", fontWeight: "500",
            lineHeight: "1.5", height: "10%", color: 'white'
        }}>
            <br/>
            <Stack direction="row" spacing={2} alignItems='center' justifyItems='center' justifyContent='center'>
                <Typography variant="subtitle1">
                    Ticker or Company Name
                </Typography>
                <TextField
                    className="custom-textfield"
                    variant="outlined"
                    value={query}
                    onChange={(e) => setQuery(e.target.value)}
                    InputLabelProps={{className: 'custom-label'}}
                />
                <Button variant="contained" sx={{"&.Mui-disabled": {background: "#c0c0c0", color: "#ffffff"}}}
                        onClick={() => setSearchTerm(query)} disabled={query.length <= 0}>
                    Search
                </Button>
            </Stack>
            <br/>
            {isLoading && (<Typography variant="h5" className="gray-text">Loading...</Typography>)}
            {!isLoading && companies.length > 0 && (corporateRiskTabs)}
        </div>
    )
}

const getCorporateRiskTabs = (user, project, companies, value, setValue, isLoading, chartRef) => {
    const handleChange = (event, newValue) => {
        setValue(newValue);
    };

    function a11yProps(index) {
        return {
            id: `simple-tab-${index}`,
            'aria-controls': `simple-tabpanel-${index}`,
        };
    }

    if (!companies || companies.length === 0) return [];
    console.log(`companies: ${companies}`);
    return (
        companies.map(
            (company, index) => {
                const tabLabels = ['Summary', 'Exposure', 'Resilience', 'Risk']
                const corpRiskSummary = CorporateRiskSummary(company, chartRef, isLoading);
                const corpExposureSummary = CorporateExposureSummary(company, chartRef, isLoading);
                const corpResilienceSummary = CorporateResilienceSummary(company, isLoading);
                const corpRisk = CorporateRisk(company, chartRef, isLoading);

                return (
                    <>
                        <Box>
                            <table style={{width: "100%"}}>
                               <tbody>
                               <tr>
                                   <td style={{alignItems: "left"}}>
                                       <Typography variant="h3" className="gray-text">{company.company_name}</Typography>
                                       <Typography variant="h5" className="gray-text">{company.ticker}</Typography>
                                   </td>
                                   <td style={{textAlign: "right"}}>
                                       <Button variant="contained"
                                               onClick={() => handleResilienceSourceDownload(user, project, company.ticker)}>
                                           Download Report
                                       </Button>
                                   </td>
                               </tr>
                               </tbody>
                            </table>
                        </Box>
                        <Box sx={{width: '100%', textAlign: 'center'}}>
                            <Box sx={{borderBottom: 1, borderColor: 'gray'}}>
                                <Tabs value={value} onChange={handleChange} aria-label="dashboard tabs"
                                      textColor="inherit"
                                      indicatorColor="primary">
                                    {tabLabels.map((label, index) => (
                                        <Tab key={label} label={label}{...a11yProps(index)}
                                             sx={{
                                                 color: value === index ? 'white' : 'gray',
                                                 fontWeight: value === index ? 'bold' : 'normal',
                                             }}
                                        />
                                    ))}
                                </Tabs>
                            </Box>
                            <CustomTabPanel value={value} index={0}>
                                {corpRiskSummary}
                            </CustomTabPanel>
                            <CustomTabPanel value={value} index={1}>
                                {corpExposureSummary}
                            </CustomTabPanel>
                            <CustomTabPanel value={value} index={2}>
                                {corpResilienceSummary}
                            </CustomTabPanel>
                            <CustomTabPanel value={value} index={3}>
                                {corpRisk}
                            </CustomTabPanel>
                        </Box>
                    </>
                )
            }
        )
    )
}

const CustomTabPanel = (props) => {
    const {children, value, index, ...other} = props;

    return (
        <div
            role="tabpanel"
            hidden={value !== index}
            id={`simple-tabpanel-${index}`}
            aria-labelledby={`simple-tab-${index}`}
            {...other}
        >
            {value === index && <Box sx={{p: 3}}>{children}</Box>}
        </div>
    );
}

CustomTabPanel.propTypes = {
    children: PropTypes.node,
    index: PropTypes.number.isRequired,
    value: PropTypes.number.isRequired,
};

const fetchCorporateRisk = (user, project, query, dataSetter, setIsLoading) => {
    setIsLoading(true);
    user && user.getIdTokenResult()
        .then((token) => {
            if (!!token.claims.api_key) {
                fetch(`${ce_default_url}/api/financial/resilience?q=${query}`, {
                    method: 'GET',
                    headers: {
                        'X-SustGlobal-APIKey': token.claims.api_key,
                        'X-SustGlobal-Project': `${project}`,
                    }
                })
                    .then(r => r.json())
                    .then(r => dataSetter(r))
            } else {
                console.error('api key not available for user')
            }
        })
        .catch((error) => {
            console.error(`error while fetching from API: ${error}`)
        })
        .finally(() => setIsLoading(false))
}

const handleResilienceSourceDownload = (user, project, ticker) => {
    user && user.getIdTokenResult()
        .then((token) => {
            if (!!token.claims.api_key) {
                fetch(`${ce_default_url}/api/financial/resilience/download/${ticker}`, {
                    method: 'GET',
                    headers: {
                        'X-SustGlobal-APIKey': token.claims.api_key,
                        'X-SustGlobal-Project': `${project}`,
                    },
                })
                    .then(async (response) => {
                        const contentDisposition = response.headers.get('content-disposition');
                        const fileName = contentDisposition
                            ? contentDisposition.split('filename=')[1]
                            : `${ticker}.json`;

                        const blob = await response.blob();
                        const url = window.URL.createObjectURL(blob);
                        const link = document.createElement('a');
                        link.href = url;
                        link.download = `${fileName}`;
                        document.body.appendChild(link);
                        link.click();
                        link.remove()
                        window.URL.revokeObjectURL(url);
                    })
                    .catch((error) => {
                        console.error(`error while fetching from API: ${error}`)
                    });
            } else {
                console.error('api key not available for user')
            }
        })
        .catch((error) => {
            console.error(`error while fetching from API: ${error}`)
        })
}


export default CorporateRiskDashboard;